import { zodResolver } from '@hookform/resolvers/zod';
import TextField from '@mui/material/TextField';
import { useForm } from 'react-hook-form';

import { Field } from '@/components/ui/Field';

import { renameSectionSchema, type RenameSectionValues } from './arcSchemas';
import type { ArcSection } from './arcTypes';
import { FormDialog } from './FormDialog';

interface RenameSectionDialogProps {
  section: ArcSection;
  /** Renames the section in the working copy; Save Draft saves it. */
  onApply: (name: string) => void;
  onClose: () => void;
}

/** Renames any section, standard ones included (E.2). */
export function RenameSectionDialog({
  section,
  onApply,
  onClose,
}: RenameSectionDialogProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<RenameSectionValues>({
    resolver: zodResolver(renameSectionSchema),
    defaultValues: { name: section.name },
  });

  const submit = handleSubmit(({ name }) => {
    if (name !== section.name) onApply(name);
    onClose();
  });

  return (
    <FormDialog
      open
      title={`Rename “${section.name}”`}
      dirty={isDirty}
      pending={false}
      failure={null}
      submitLabel="Rename"
      onSubmit={(event) => void submit(event)}
      onClose={onClose}
      maxWidth="xs"
      testId="arc-builder-rename-section-dialog"
    >
      <Field
        label="Section name"
        hint="Up to 80 characters"
        error={errors.name?.message}
        testId="arc-builder-rename-section-dialog-name"
      >
        <TextField {...register('name')} size="small" fullWidth />
      </Field>
    </FormDialog>
  );
}
